/**
 * Map cart-response `kount_environment` to the Kount SDK environment param.
 *
 * Returns `undefined` while the cart query is loading or errored — callers
 * pass that into `useKount`, which no-ops on `undefined` so no DDC session
 * is created before we know the merchant's environment.
 *
 * Returns `"TEST"` when the merchant is on sandbox (or the field is missing
 * from a pre-rollout cart response — graceful fallback for the BE→FE deploy
 * gap window) and `"PROD"` when the merchant is on production.
 */
export type KountSdkEnvironment = "TEST" | "PROD";

interface CartQueryShape {
  isLoading: boolean;
  isError: boolean;
  data?: {
    cart?: {
      company?: {
        kount_environment?: "sandbox" | "production" | null;
        kount_client_id?: string | null;
      };
    };
  } | null;
}

export function resolveKountSdkEnvironment(
  query: CartQueryShape,
): KountSdkEnvironment | undefined {
  if (query.isLoading || query.isError || !query.data) return undefined;
  const env = query.data?.cart?.company?.kount_environment ?? "sandbox";
  return env === "production" ? "PROD" : "TEST";
}

/**
 * Resolve the Kount Client ID (merchant MID) from the cart query.
 *
 * Enterprise merchants carry their own Client ID on the cart-response company
 * block. Portfolio merchants return null — `useKount` falls back to the global
 * `NEXT_PUBLIC_KOUNT_CLIENT_ID` env var.
 *
 * Returns `undefined` while the cart query is loading or errored (treat as
 * "use env var fallback"); returns the merchant's Client ID when present, or
 * `undefined` when the merchant is portfolio.
 */
export function resolveKountClientId(
  query: CartQueryShape,
): string | undefined {
  if (query.isLoading || query.isError || !query.data) return undefined;
  return query.data?.cart?.company?.kount_client_id ?? undefined;
}
